<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full">
      <!-- Logo/Brand -->
      <div class="text-center mb-8">
        <img src="/deepchat-logo.svg" alt="DeepChat" class="mx-auto mb-4 h-20 w-20 object-contain" />
        <h2 class="text-3xl font-bold text-gray-900 dark:text-white">
          Logga in på DeepChat
        </h2>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Använd e-post och lösenord — eller en magisk länk till din inbox.
        </p>
      </div>

      <!-- Login Card -->
      <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-8">
        <!-- Magic link sent state -->
        <div v-if="magicLinkSent" class="text-center py-4">
          <div class="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-4">
            <svg class="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Kolla din e-post</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Vi har skickat en inloggningslänk till <strong class="text-gray-900 dark:text-white">{{ email }}</strong>
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-500 mb-6">
            Länken gäller i 15 minuter.
          </p>
          <button
            @click="magicLinkSent = false; error = ''; success = ''"
            class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500"
          >
            Använd en annan e-post
          </button>
        </div>

        <!-- Main login form -->
        <div v-else>
          <!-- Password login (primary) -->
          <form class="space-y-4" @submit.prevent="handleLogin">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                E-postadress
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autocomplete="email"
                required
                v-model="email"
                class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="du@foretag.se"
              />
            </div>

            <div>
              <div class="flex items-center justify-between mb-2">
                <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Lösenord
                </label>
                <button type="button" @click="handleForgotPassword" class="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500">
                  Glömt lösenord?
                </button>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autocomplete="current-password"
                required
                v-model="password"
                class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="Ditt lösenord"
              />
            </div>

            <button
              type="submit"
              :disabled="loading || !email || !password"
              class="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <span v-if="loading && loadingAction === 'login'" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loggar in…
              </span>
              <span v-else>Logga in</span>
            </button>
          </form>

          <!-- Divider -->
          <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-200 dark:border-gray-700"></div>
            </div>
            <div class="relative flex justify-center text-xs uppercase tracking-wide">
              <span class="bg-white dark:bg-gray-800 px-3 text-gray-500 dark:text-gray-400">eller</span>
            </div>
          </div>

          <!-- Magic link (alternative) -->
          <button
            type="button"
            @click="handleMagicLink"
            :disabled="loading || !email"
            class="w-full flex justify-center items-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="loading && loadingAction === 'magic'" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Skickar länk…
            </span>
            <span v-else class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Skicka magisk länk till min e-post
            </span>
          </button>
          <p v-if="!email" class="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
            Fyll i din e-post för att använda magisk länk
          </p>

          <!-- Error / Success messages -->
          <div v-if="error" class="mt-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4">
            <p class="text-sm font-medium text-red-800 dark:text-red-200">{{ error }}</p>
          </div>
          <div v-if="success" class="mt-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 p-4">
            <p class="text-sm font-medium text-emerald-800 dark:text-emerald-200">{{ success }}</p>
          </div>
        </div>

        <!-- Sign up link -->
        <div class="mt-6 text-center border-t border-gray-200 dark:border-gray-700 pt-6">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Har du inget konto?
            <router-link to="/register" class="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300">
              Skapa konto gratis
            </router-link>
          </p>
          <p class="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
            Portal v1.0.2
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const loading = ref(false)
const loadingAction = ref<'login' | 'magic' | 'reset' | ''>('')
const error = ref('')
const success = ref('')
const magicLinkSent = ref(false)

const handleMagicLink = async () => {
  if (!email.value) return
  loading.value = true
  loadingAction.value = 'magic'
  error.value = ''
  success.value = ''
  try {
    await authStore.requestMagicLink(email.value)
    magicLinkSent.value = true
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Kunde inte skicka inloggningslänk.'
  } finally {
    loading.value = false
    loadingAction.value = ''
  }
}

const handleLogin = async () => {
  loading.value = true
  loadingAction.value = 'login'
  error.value = ''
  success.value = ''
  try {
    await authStore.login(email.value, password.value)
    router.push('/dashboard')
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Inloggningen misslyckades. Kontrollera e-post och lösenord.'
  } finally {
    loading.value = false
    loadingAction.value = ''
  }
}

const handleForgotPassword = async () => {
  if (!email.value) {
    error.value = 'Fyll i din e-postadress först.'
    return
  }
  loading.value = true
  loadingAction.value = 'reset'
  error.value = ''
  try {
    await authStore.requestPasswordReset(email.value)
    success.value = 'Om e-posten finns har en återställningslänk skickats.'
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Kunde inte begära lösenordsåterställning.'
  } finally {
    loading.value = false
    loadingAction.value = ''
  }
}

onMounted(async () => {
  const verifyToken = typeof route.query.verify === 'string' ? route.query.verify : ''
  const magicToken = typeof route.query.magic === 'string' ? route.query.magic : ''
  const resetToken = typeof route.query.reset === 'string' ? route.query.reset : ''
  const impersonationToken = typeof route.query.impersonationToken === 'string' ? route.query.impersonationToken : ''

  try {
    if (impersonationToken) {
      // Handle impersonation from superadmin
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.deepchat.se'}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${impersonationToken}` }
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          authStore.setAuthData(impersonationToken, data.data.user, data.data.company)
          router.replace('/dashboard')
          return
        }
      }
    }
    if (verifyToken) {
      await authStore.verifyEmail(verifyToken)
      success.value = 'E-post verifierad! Du kan nu logga in.'
      router.replace('/login')
    }
    if (magicToken) {
      await authStore.verifyMagicLink(magicToken)
      router.replace('/dashboard')
    }
    if (resetToken) {
      const newPassword = window.prompt('Ange nytt lösenord (minst 8 tecken)')
      if (newPassword) {
        await authStore.resetPassword(resetToken, newPassword)
        success.value = 'Lösenordet är återställt. Du kan nu logga in.'
        router.replace('/login')
      }
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Länken kunde inte användas.'
  }
})
</script>
